import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSurahList } from '@/hooks/useSurah';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils/cn';

/**
 * Pre-session form: pick a surah, pick a start ayah, pick an end ayah.
 *
 * Validates `start <= end` and the range stays within the surah's
 * `ayah_count`. On submit, calls `onStart` with the chosen range.
 *
 * The parent (Memorize.tsx) decides what to do with the result —
 * typically: call `useMemorizationSession.startSession(...)`.
 */

type SessionSetupProps = {
  /** Pre-populate fields (e.g. when resuming after End Session). */
  defaultSurahId?: number;
  defaultStartAyah?: number;
  defaultEndAyah?: number;
  onStart: (args: {
    surahId: number;
    startAyah: number;
    endAyah: number;
  }) => void;
};

const selectClassName = cn(
  'w-full h-11 px-3 rounded-lg',
  'bg-white dark:bg-ink-900',
  'border border-ink-200 dark:border-ink-800',
  'text-ink-900 dark:text-ink-50 text-sm font-medium',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  'transition-colors',
);

export function SessionSetup({
  defaultSurahId = 1,
  defaultStartAyah = 1,
  defaultEndAyah,
  onStart,
}: SessionSetupProps) {
  const surahListQuery = useSurahList();

  const [surahId, setSurahId] = useState<number>(defaultSurahId);
  const [startAyah, setStartAyah] = useState<number>(defaultStartAyah);
  const [endAyah, setEndAyah] = useState<number>(defaultEndAyah ?? 1);

  const currentSurah = useMemo(
    () => surahListQuery.data?.find((s) => s.id === surahId) ?? null,
    [surahListQuery.data, surahId],
  );
  const ayahCount = currentSurah?.ayah_count ?? 0;

  // When surah changes, reset range to full-surah default.
  useEffect(() => {
    if (!currentSurah) return;
    setStartAyah(1);
    setEndAyah(currentSurah.ayah_count);
  }, [currentSurah]);

  // Keep startAyah <= endAyah; clamp endAyah to ayah_count.
  useEffect(() => {
    if (ayahCount === 0) return;
    if (endAyah > ayahCount) setEndAyah(ayahCount);
    if (startAyah > ayahCount) setStartAyah(ayahCount);
    if (startAyah > endAyah) setEndAyah(startAyah);
  }, [ayahCount, startAyah, endAyah]);

  if (surahListQuery.isError) {
    return (
      <EmptyState
        tone="error"
        title="Couldn't load the surah list."
        description={surahListQuery.error?.message ?? 'Try again.'}
        onRetry={() => surahListQuery.refetch()}
      />
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!currentSurah) return;
    onStart({ surahId, startAyah, endAyah });
  }

  const disabled =
    surahListQuery.isLoading || !currentSurah || startAyah > endAyah;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      <p className="text-sm text-ink-600 dark:text-ink-300 leading-relaxed">
        Choose a surah and an ayah range. Progress is saved locally in
        your browser — no signin required for this beta. Audio is
        Mishary Rashid Alafasy.
      </p>

      <label className="block">
        <span className="block text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400 mb-2">
          Surah
        </span>
        {surahListQuery.isLoading ? (
          <Skeleton className="h-11 w-full rounded-lg" />
        ) : (
          <select
            value={surahId}
            onChange={(e) => setSurahId(Number(e.target.value))}
            className={selectClassName}
            aria-label="Select surah"
          >
            {surahListQuery.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.id}. {s.name_en} ({s.name_ar}) · {s.ayah_count} ayahs
              </option>
            ))}
          </select>
        )}
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400 mb-2">
            Start ayah
          </span>
          <select
            value={startAyah}
            onChange={(e) => setStartAyah(Number(e.target.value))}
            disabled={ayahCount === 0}
            className={selectClassName}
            aria-label="Select start ayah"
          >
            {Array.from({ length: ayahCount }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400 mb-2">
            End ayah
          </span>
          <select
            value={endAyah}
            onChange={(e) => setEndAyah(Number(e.target.value))}
            disabled={ayahCount === 0}
            className={selectClassName}
            aria-label="Select end ayah"
          >
            {Array.from({ length: ayahCount }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </label>
      </div>

      {startAyah > endAyah && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          Start ayah must be ≤ end ayah.
        </p>
      )}

      <div className="flex items-center gap-3 pt-2">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={disabled}
        >
          Start session
        </Button>
        {currentSurah && (
          <p className="text-sm text-ink-500 dark:text-ink-400">
            {endAyah - startAyah + 1} ayah
            {endAyah - startAyah + 1 === 1 ? '' : 's'} in this session
          </p>
        )}
      </div>
    </form>
  );
}
